import { store$, store } from '@redux/store';
import { distinctUntilChanged, filter, map, fromEvent, merge, withLatestFrom } from 'rxjs';
import fastdom from 'fastdom';
import { distinctUntilKeyChanged, pairwise } from 'rxjs/operators';
import { ProductType, selectListingCount, selectProductType } from 'features/searchResultsFacets';
import { interactiveSitePlanSlice } from '../redux/slices';
import {
  selectedLotSummaryCard,
  selectZoomLevel,
  selectLotNumber,
  selectAvailableSpec,
  selectAvailablePlans,
  selectLotInformation,
  selectLotCommunityIdAndSitePlans,
  selectLotStatus,
  selectLotId,
} from '../redux/selectors';
import { zoomLevelTriggers } from '../constants/sitePlansConstants';
import PaidInteractiveSitePlanCommunityCard from './summaryCard/paidInteractiveSitePlanCommunityCard';
import PaidInteractiveSitePlanHousingUnitCard from './summaryCard/paidInteractiveSitePlanHousingUnitCard';
import CommunityLabel from './summaryCard/communityLabel';
import HomeLabel from './summaryCard/homeLabel';
import LotStatus from '../enums/lotStatus';

const interactiveSitePlanActions = interactiveSitePlanSlice.actions;
const summaryCardContainer = document.querySelector('[data-map-carousel]');
const closeButton = document.querySelector('[data-summary-card-close]');

/**
 * dispatchClearSummaryCardAction function will dispatch an action for clearing
 * the lot summary card store related information.
 */
const dispatchClearSummaryCardAction = () => {
  store.dispatch(interactiveSitePlanActions.clearInteractiveSitePlanCard());
  store.dispatch(interactiveSitePlanActions.removeLotInformation());
};

/**
 * closeLotSummaryCard is in charge of closing the lot listing summary card.
 */
const closeLotSummaryCard = () => {
  const cardsContainer = document.querySelector('[data-summary-card]');
  if (cardsContainer) {
    fastdom.mutate(() => {
      cardsContainer.innerHTML = '';
      summaryCardContainer.setAttribute('hidden', '');
      summaryCardContainer.classList.remove('nhs-u-width--full', 'nhs-u-pointer-events--none');
      cardsContainer.classList.remove('nhs-c-carousel__container--lots', 'nhs-mq-carousel__container--lots-medium');
      closeButton.classList.remove('nhs-mq-carousel__close--lot-card-medium', 'nhs-u-pointer-events--all');
    });
  }

  const event = new CustomEvent('interactiveSitePlanSummaryCardClosed');
  store.dispatch(interactiveSitePlanActions.removeLotInformation());
  setTimeout(() => {
    document.dispatchEvent(event);
  }, 0);
};

/**
 * closePreviousSummaryCard will close the visible summary card when the store no longer
 * has information to keep it opened.
 */
const closePreviousSummaryCard = ({ previousSummaryCard, currentSummaryCard }) => {
  if (!currentSummaryCard && previousSummaryCard) {
    setTimeout(() => closeLotSummaryCard(currentSummaryCard), 0);
  }
};

/**
 * renderSummaryCard prepares the HTML for the lot summary card using the provided summaryCard data and current state.
*/
const renderSummaryCard = (summaryCard) => {
  const state = store.getState();
  const lotNumber = selectLotNumber(state);
  const availableSpecCount = selectAvailableSpec(state) ? 1 : 0;
  const availablePlanCount = selectAvailablePlans(state)?.length;
  const lotStatus = selectLotStatus(state);
  const lotId = selectLotId(state);
  const container = document.querySelector('[data-summary-card]');

  fastdom.mutate(() => {
    container.innerHTML = '';
    summaryCardContainer.removeAttribute('hidden');
    summaryCardContainer.classList.add('nhs-u-width--full', 'nhs-u-pointer-events--none');
    container.classList.add('nhs-c-carousel__container--lots', 'nhs-mq-carousel__container--lots-medium');
    closeButton.classList.add('nhs-mq-carousel__close--lot-card-medium', 'nhs-u-pointer-events--all');
    closeButton?.removeAttribute('hidden');
  });

  const card =
    availableSpecCount === 1 && lotStatus !== LotStatus.Sold.toString()
      ? new PaidInteractiveSitePlanHousingUnitCard(summaryCard)
      : new PaidInteractiveSitePlanCommunityCard(summaryCard);

  const label = availableSpecCount === 1 ? new HomeLabel() : new CommunityLabel();

  label.setInformation(summaryCard);
  card.setInformation({ summaryCard, lotNumber, availableSpecCount, availablePlanCount, lotStatus, lotId });
  card.setLabel(label);

  const fragment = document.createDocumentFragment();

  const html = card.getHtml();
  const range = document.createRange();
  const documentFragment = range.createContextualFragment(html);
  fragment.appendChild(documentFragment);

  fastdom.mutate(() => {
    container.appendChild(fragment);
    card.bindEvents();
  });
};

closeButton?.addEventListener('click', dispatchClearSummaryCardAction);

/**
 * selectedLotSummaryCard$ will emit a value on any changes from the store
 * related to the lot listings summary card.
 */
const selectedLotSummaryCard$ = store$.pipe(map(selectedLotSummaryCard), distinctUntilChanged());

/**
 * lotSummaryCardSelected$ will stream a value when there is new informatino
 * available in the store to build a lot listings summary card.
 */
const lotSummaryCardSelected$ = store$.pipe(map(selectedLotSummaryCard), distinctUntilChanged(), filter(Boolean));

/**
 * onCloseCard$ will emit a value when the actual summary card needs to be
 * removed as there is no information in the store to keep it open.
 */
const onCloseCard$ = selectedLotSummaryCard$.pipe(
  pairwise(),
  map(([previousSummaryCard, currentSummaryCard]) => ({
    previousSummaryCard,
    currentSummaryCard,
  }))
);

/**
 * hamburgerMenuClicked$ will emit values when hamburger menu is clicked.
 */
const hamburgerMenuClicked$ = fromEvent(document.body, 'hamburgerMenuClicked');

/**
 * advancedFiltersClicked$ will emit values when advanced filters are clicked.
 */
const advancedFiltersClicked$ = fromEvent(document.body, 'advancedFiltersClicked');

/**
 * ListViewToggled$ will emit values when list view toggle button is clicked.
 */
const ListViewToggled$ = fromEvent(document.body, 'listViewToggled');

/**
 * cardCloseClick$ will stream a value when users close the lot summary card.
 */
const cardCloseClick$ = fromEvent(document, 'interactiveSitePlanSummaryCardClosed');

/**
 * onExitInteractiveState$ will stream a value when either the whole community exited locked
 * state, or a previous lot in locked state exited that state.
 */
const onExitInteractiveState$ = store$.pipe(
  map(selectZoomLevel),
  distinctUntilChanged(),
  filter((zoomLevel) => zoomLevel < zoomLevelTriggers.zoomLevelEnterInteractiveStateTrigger)
);

/**
 * onNoResults$ will emit a value every time there are no listings in the store.
 */
const onNoResults$ = store$.pipe(
  map(selectListingCount),
  distinctUntilChanged(),
  filter((listingCount) => listingCount === 0)
);

/**
 * onRemovedInteractiveSitePlan$ will emit every time an Interactive Site Plan, who had an open
 * lot summary card, gets removed from the store.
 */
const onRemovedInteractiveSitePlan$ = store$.pipe(
  map(selectLotCommunityIdAndSitePlans),
  distinctUntilChanged(),
  filter(({ lotCommunityId, sitePlans }) => !sitePlans.some((sitePlan) => sitePlan.communityId === lotCommunityId))
);

/**
 * onProductTypeDistinctThanHomes$ will emit a value every time the product type changes while a site plan is shown
 * in interactive state. The change we target in this observable is any product type distinct than "Homes"
 */
const onProductTypeDistinctThanHomes$ = store$.pipe(
  filter((state) => selectZoomLevel(state) >= zoomLevelTriggers.zoomLevelEnterInteractiveStateTrigger),
  map(selectProductType),
  distinctUntilChanged(),
  filter((productType) => productType !== ProductType.Homes.toString())
);

/**
 * onActiveLotChanged$ emits the summary card information whenever the active lot changes in the store.
 * It listens for changes to the lotId in the lot information, and emits the updated summaryCard object.
 * This observable is useful for updating the summary card UI when the user selects a different lot.
 */
const onActiveLotChanged$ = store$.pipe(
  map(selectLotInformation),
  distinctUntilKeyChanged('lotId'),
  map(({ summaryCard }) => summaryCard),
  filter(Boolean)
);

/**
 * onCloseSummaryCard$ will stream if any of the reassons to clear the lot listing summary card happens.
 */
const onCloseSummaryCard$ = merge(
  cardCloseClick$,
  hamburgerMenuClicked$,
  advancedFiltersClicked$,
  ListViewToggled$,
  onExitInteractiveState$,
  onNoResults$,
  onRemovedInteractiveSitePlan$,
  onProductTypeDistinctThanHomes$
).pipe(
  withLatestFrom(selectedLotSummaryCard$),
  filter(([, summaryCard]) => Boolean(summaryCard))
);

onCloseSummaryCard$.subscribe(dispatchClearSummaryCardAction);
lotSummaryCardSelected$.subscribe(renderSummaryCard);
onCloseCard$.subscribe(closePreviousSummaryCard);
onActiveLotChanged$.subscribe(renderSummaryCard);
